import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiList, FiX } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const FinanceAuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await financeAPI.auditLog.list(actionFilter ? { action: actionFilter } : undefined);
      setEntries(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load the finance audit log.');
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);

  const actions = [...new Set(entries.map((e) => e.action))].sort();

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiList size={22} />Finance Audit Log</h1>
          <p style={fs.pageSubtitle}>Read-only, Chairman/Finance-Director-only. Every high-risk action and money-movement decision in the finance module lands here.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={fs.select}>
            <option value="">All actions</option>
            {actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={fs.emptyState}><FiList size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No audit log entries yet.</div></div>
      ) : (
        <div style={fs.tableWrap}>
          <table style={fs.table}>
            <thead><tr style={fs.tableHeadRow}>{['When', 'Actor', 'Action', 'Target', 'IP', 'Notes'].map((h) => <th key={h} style={fs.th}>{h}</th>)}</tr></thead>
            <tbody>
              {entries.map((e, i) => {
                const expanded = expandedId === e.id;
                return (
                  <tr key={e.id} onClick={() => setExpandedId(expanded ? null : e.id)} style={{ borderBottom: i < entries.length - 1 ? '1px solid #111' : 'none', cursor: (e.before_value || e.after_value) ? 'pointer' : 'default' }}>
                    <td style={fs.td}>{new Date(e.occurred_at).toLocaleString()}</td>
                    <td style={{ ...fs.td, color: fs.colors.textBright }}>{e.actor_username || '—'}</td>
                    <td style={fs.td}><span style={fs.badge(fs.colors.purple)}>{e.action}</span></td>
                    <td style={fs.td}>{e.target_content_type ? `${e.target_content_type} #${e.target_object_id}` : '—'}</td>
                    <td style={fs.td}>{e.ip_address || '—'}</td>
                    <td style={fs.td}>
                      {e.notes || '—'}
                      {expanded && (e.before_value || e.after_value) && (
                        <pre style={{ marginTop: 6, background: fs.colors.input, padding: 8, borderRadius: 6, fontSize: 11, color: fs.colors.textDim, whiteSpace: 'pre-wrap', maxWidth: 420 }}>
                          {e.before_value && `before: ${JSON.stringify(e.before_value)}\n`}
                          {e.after_value && `after: ${JSON.stringify(e.after_value)}`}
                        </pre>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FinanceAuditLog;
